import { _get } from "./fetch";
import { getLicenseValidityURL } from "./apiUrl";
import { licenseCache } from "./cache";

export function normalizeRecordId(value) {
  if (!value) return "";
  if (typeof value === "object") {
    return String(value._id || value.id || value.branchId || "").trim();
  }
  return String(value).trim();
}

function pickExpiryField(source) {
  if (!source || typeof source !== "object") return null;

  const candidates = [
    source.expiryDate,
    source.expiry,
    source.licenseExpiry,
    source.licenceExpiry
  ];

  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== "") {
      return String(candidate);
    }
  }

  return null;
}

/**
 * Finds the branch-specific license detail object inside common API wrappers.
 */
export function resolveLicenseDetailFromResponse(response) {
  if (!response || typeof response !== "object") {
    return null;
  }

  const candidates = [
    response?.licenseDetail,
    response?.data?.licenseDetail,
    response?.data,
    response?.response,
    response
  ];

  return candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      pickExpiryField(candidate)
  ) || null;
}

/**
 * Normalizes a license API response into a branch-specific detail object for caching.
 */
export function normalizeLicenseApiResponse(response) {
  return resolveLicenseDetailFromResponse(response);
}

/**
 * Reads expiry from common API shapes. Missing or empty => null (caller treats as unknown).
 */
export function getLicenseExpiryDateString(licenseData) {
  if (!licenseData || typeof licenseData !== "object") return null;

  const resolvedDetail = resolveLicenseDetailFromResponse(licenseData);
  if (resolvedDetail) {
    const resolvedExpiry = pickExpiryField(resolvedDetail);
    if (resolvedExpiry) return resolvedExpiry;
  }

  return (
    pickExpiryField(licenseData) ||
    pickExpiryField(licenseData.data) ||
    pickExpiryField(licenseData.response)
  );
}

/** True only when a real expiry exists and is in the past. */
export function isLicenseExpiredByExpiry(licenseData) {
  const str = getLicenseExpiryDateString(licenseData);
  if (!str) return false;
  const t = new Date(str).getTime();
  if (isNaN(t)) return false;
  return t < Date.now();
}

export function normalizeBranchStatus(status) {
  return status ? String(status).toLowerCase().trim() : "";
}

export function isInactiveOrExpiredBranchStatus(status) {
  const branchStatus = normalizeBranchStatus(status);
  return branchStatus === "inactive" ||
    branchStatus === "expired" ||
    branchStatus === "expire" ||
    branchStatus.includes("expired") ||
    branchStatus.includes("inactive");
}

export function isLicenseExpiredForClient(licenseData) {
  if (!licenseData || typeof licenseData !== "object") return false;
  if (licenseData.success === false) return false;

  const expiryDate = getLicenseExpiryDateString(licenseData);
  // Match licenseValidity.js: missing expiry is treated as expired.
  if (!expiryDate) return true;

  const futureDate = new Date(expiryDate).getTime();
  // Match card UI: unparseable expiry is treated as expired ("License has Expired").
  if (isNaN(futureDate)) return true;

  return futureDate < Date.now();
}

export function isLicenseNearToExpireForClient(licenseData) {
  const expiryDate = getLicenseExpiryDateString(licenseData);
  if (!expiryDate) return false;
  const futureDate = new Date(expiryDate).getTime();
  if (isNaN(futureDate)) return false;
  const days = Math.floor((futureDate - Date.now()) / (1000 * 60 * 60 * 24));
  return days >= 0 && days <= 3;
}

export function hasBranchLicenseLookup(branch) {
  const branchId = normalizeRecordId(branch?._id || branch?.id);
  return Boolean(branchId && branch?.company);
}

export function hasBranchLicenseResult(branchId, licenseDataMap = {}) {
  return Object.prototype.hasOwnProperty.call(licenseDataMap, normalizeRecordId(branchId));
}

export function isBranchExpiredForClient(branch, licenseDataMap = {}) {
  if (isInactiveOrExpiredBranchStatus(branch?.status)) {
    return true;
  }

  const branchId = normalizeRecordId(branch?._id || branch?.id);
  if (!hasBranchLicenseLookup(branch) || !hasBranchLicenseResult(branchId, licenseDataMap)) {
    return false;
  }

  const licenseData = licenseDataMap[branchId];
  // Completed fetch with no usable license detail => Expired (match licenseValidity.js).
  if (!licenseData) {
    return true;
  }

  return isLicenseExpiredForClient(licenseData);
}

export function isBranchActiveForClient(branch, licenseDataMap = {}) {
  if (normalizeBranchStatus(branch?.status) !== "active") {
    return false;
  }

  return !isBranchExpiredForClient(branch, licenseDataMap);
}

export function isBranchNearToExpireForClient(branch, licenseDataMap = {}) {
  const branchId = normalizeRecordId(branch?._id || branch?.id);
  if (!isBranchActiveForClient(branch, licenseDataMap)) {
    return false;
  }

  return isLicenseNearToExpireForClient(licenseDataMap[branchId]);
}

export function toClientStatusFilterParam(filter) {
  if (filter === "Expired") return "expired";
  if (filter === "NearToExpire") return "neartoexpire";
  return String(filter || "active").toLowerCase();
}

/**
 * Same license lookup used by Web App Clients:
 * production admin /branches/:id/license-detail
 */
export async function fetchBranchLicenseDetail(branchOrId) {
  const branchId = normalizeRecordId(
    typeof branchOrId === "object"
      ? (branchOrId?._id || branchOrId?.id || branchOrId)
      : branchOrId
  );
  const companyId = normalizeRecordId(
    typeof branchOrId === "object" ? branchOrId?.company : ""
  );

  if (!branchId) {
    return null;
  }

  const cacheKey = companyId ? `v2_${companyId}_${branchId}` : `v2_${branchId}`;
  const cached = licenseCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const response = await _get(getLicenseValidityURL(branchId), { useCache: false });
    const responseData = normalizeLicenseApiResponse(response);
    if (responseData) {
      licenseCache.set(cacheKey, responseData);
      return responseData;
    }
  } catch {
    return null;
  }

  return null;
}
